/**
 * WineCard.js — The Wine Engine wine listing card (cardType "wine").
 *
 * OVERRIDES: src/components/base/WineCard.js
 *
 * Pixel/behaviour parity with old createWineListing
 * (RedConcierge/static/javascript/main.js ~3717–3804):
 *   discount badge → image → "Tasting Notes" pill → title → country|alcohol|type
 *   → price + price_unit + "Per bottle" | "Add to Basket" (always that label;
 *   aria/title may use wine.cta_text). In-cart → trash + qty steppers.
 *
 * Cart: emits ws-cart-* (features/optional/cart.js, wired in wineengine/index.js).
 */

import { emit, on, getState,isChatDisabled } from "../../../core/state.js";
import { bindWineDrawerOpen } from "../../../utils/openWineDrawer.js";

const TASTING_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="15" viewBox="0 0 12 15" fill="none" aria-hidden="true"><path d="M3.16667 0.5V3.16667M5.83333 0.5V3.16667M8.5 0.5V3.16667M3.16667 5.83333H7.16667M3.16667 8.5H8.5M3.16667 11.1667H6.5M1.83333 1.83333H9.83333C10.5697 1.83333 11.1667 2.43029 11.1667 3.16667V12.5C11.1667 13.2364 10.5697 13.8333 9.83333 13.8333H1.83333C1.09695 13.8333 0.5 13.2364 0.5 12.5V3.16667C0.5 2.43029 1.09695 1.83333 1.83333 1.83333Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const TRASH_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4.00004H14M12.6667 4.00004V13.3334C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3334V4.00004M5.33333 4.00004V2.66671C5.33333 2.00004 6 1.33337 6.66667 1.33337H9.33333C10 1.33337 10.6667 2.00004 10.6667 2.66671V4.00004" stroke="#0A0A0A" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

function parseMoney(value) {
  if (value == null || value === "") return NaN;
  return parseFloat(String(value).replace(/[^0-9.]/g, ""));
}

function discountPercent(wine) {
  const orig = parseMoney(wine.compare_at_price);
  const curr = parseMoney(wine.regular_price);
  if (!(orig > curr && curr > 0)) return 0;
  return Math.round((1 - curr / orig) * 100);
}

function metaLine(wine) {
  const notes = wine.winemaker_notes || {};
  return [wine.country, notes.Alcohol || notes.alcohol || "", wine.wine_type || ""]
    .filter(Boolean)
    .join(" | ");
}

export default class WineCard {
  render(wine) {
    const variantId = String(wine.variant_id ?? wine["Variant SKU"] ?? "");
    const soldOut = isTrue(wine.sold_out);
    const unavailable = wine.available !== undefined && !isTrue(wine.available);
    const detailLabel =
      wine.message || `Tell me more about ${wine.title || "this wine"}`;
    const openDetail = () =>
    {
       if(isChatDisabled()) return;
      emit("ws-prompt-select", {
        label: detailLabel,
        value: detailLabel,
        uniqueId: wine.unique_id,
      });
    }
    const card = document.createElement("div");
    card.className = "ws-we-card";
    if (variantId) card.dataset.variantId = variantId;
    if (wine.unique_id != null) card.dataset.uniqueId = String(wine.unique_id);
    if (wine.wine_key) card.dataset.wineKey = String(wine.wine_key);

    // ── Sold-out branch (old createWineListing else) ──
    if (soldOut) {
      const head = document.createElement("div");
      head.className = "ws-we-product-head";
      if (wine.cover_image_link) {
        const img = document.createElement("img");
        img.src = wine.cover_image_link;
        img.alt = wine.title || "Wine";
        img.loading = "lazy";
        head.appendChild(img);
      }
      const overlay = document.createElement("div");
      overlay.className = "ws-we-soldout";
      overlay.textContent = "SOLD OUT";
      head.appendChild(overlay);
      card.appendChild(head);

      const body = document.createElement("div");
      body.className = "ws-product-body ws-we-product-body";
      if (wine.title) {
        const title = document.createElement("p");
        title.className = "ws-we-title";
        title.title = wine.title;
        title.textContent = wine.title;
        body.appendChild(title);
      }
      const details = document.createElement("div");
      details.className = "ws-we-product-details";
      const footer = document.createElement("div");
      footer.className = "ws-we-product-footer";
      if (wine.regular_price) {
        const price = document.createElement("p");
        price.textContent = wine.regular_price;
        footer.appendChild(price);
      }
      if (wine.price_unit) {
        const unit = document.createElement("p");
        unit.textContent = wine.price_unit;
        footer.appendChild(unit);
      }
      details.appendChild(footer);
      body.appendChild(details);
      card.appendChild(body);
      return card;
    }

    // ── In-stock branch ──
    const pct = discountPercent(wine);
    if (pct > 0) {
      const badge = document.createElement("div");
      badge.className = "ws-we-discount";
      badge.innerHTML = `<span class="ws-we-discount-pct">-${pct}%</span>`;
      card.appendChild(badge);
    }

    const head = document.createElement("div");
    head.className = "ws-we-product-head";
    if (wine.cover_image_link) {
      const img = document.createElement("img");
      img.src = wine.cover_image_link;
      img.alt = wine.title || "Wine";
      img.loading = "lazy";
      img.style.cursor = "pointer";
      img.addEventListener("click", openDetail);
      head.appendChild(img);
    }
    card.appendChild(head);

    const body = document.createElement("div");
    body.className = "ws-we-product-body";

    const tasting = document.createElement("button");
    tasting.type = "button";
    tasting.className = "ws-we-tasting";
    tasting.setAttribute("aria-label", "Tasting notes");
    tasting.innerHTML = `${TASTING_ICON}<span>Tasting Notes</span>`;
    bindWineDrawerOpen(tasting, wine, card);
    body.appendChild(tasting);

    if (wine.title) {
      const title = document.createElement("p");
      title.className = "ws-we-title";
      title.title = wine.title;
      title.textContent = wine.title;
      body.appendChild(title);
    }

    const meta = metaLine(wine);
    if (meta) {
      const desc = document.createElement("p");
      desc.className = "ws-we-meta";
      desc.textContent = meta;
      body.appendChild(desc);
    }

    const details = document.createElement("div");
    details.className = "ws-we-product-details";

    const purchase = document.createElement("div");
    purchase.className = "ws-we-purchase-row";

    const footer = document.createElement("div");
    footer.className = "ws-we-product-footer";
    if (wine.regular_price) {
      const price = document.createElement("p");
      price.textContent = wine.regular_price;
      footer.appendChild(price);
    }
    {
      const unit = document.createElement("p");
      unit.textContent = wine.price_unit || "";
      footer.appendChild(unit);
    }
    {
      const per = document.createElement("p");
      per.textContent = "Per bottle";
      footer.appendChild(per);
    }
    purchase.appendChild(footer);

    // Old main.js: ALWAYS "Add to Basket" (never cta_text as visible label).
    if (!unavailable && variantId) {
      const actionMount = document.createElement("div");
      actionMount.className = "ws-we-action-mount";
      const renderAction = () => {
        const line = (getState().cart?.items || []).find(
          (it) => String(it.productVariantId) === String(variantId),
        );
        actionMount.replaceChildren(
          line
            ? this._buildQty(variantId, Number(line.quantity) || 1)
            : this._buildAdd(variantId, wine.cta_text),
        );
      };
      renderAction();
      on("ws-cart-count", () => {
        if (actionMount.isConnected) renderAction();
      });
      purchase.appendChild(actionMount);

      const overlay = document.createElement("div");
      overlay.className = "ws-cart-overlay";
      overlay.hidden = true;
      card.appendChild(overlay);
      actionMount.addEventListener("click", (e) => {
        if (e.target.closest("button")) overlay.hidden = false;
      });
      on("ws-cart-busy", ({ active } = {}) => {
        if (!active && card.isConnected) overlay.hidden = true;
      });
    }

    details.appendChild(purchase);
    body.appendChild(details);
    card.appendChild(body);
    return card;
  }

  _buildAdd(variantId, ctaText) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ws-we-add";
    // Visible label is always "Add to Basket" (old createWineListing).
    btn.textContent = "Add to Basket";
    const label = ctaText || "Add to Basket";
    btn.setAttribute("aria-label", label);
    btn.title = label;
    btn.dataset.variantId = variantId;
    btn.addEventListener("click", () =>
      emit("ws-cart-add", { variantId, quantity: 1 }),
    );
    return btn;
  }

  _buildQty(variantId, quantity) {
    const wrap = document.createElement("div");
    wrap.className = "ws-we-product-action product-action";

    const del = document.createElement("button");
    del.type = "button";
    del.className = "ws-we-delete delete-cart";
    del.setAttribute("aria-label", "delete from basket");
    del.title = "delete from basket";
    del.dataset.variantId = variantId;
    del.innerHTML = TRASH_ICON;
    del.addEventListener("click", () => emit("ws-cart-remove", { variantId }));

    const qtyWrap = document.createElement("div");
    qtyWrap.className = "ws-we-qty-wrap product-quantity-wrapper";

    const dec = document.createElement("button");
    dec.type = "button";
    dec.className = "ws-we-dec dec-qty";
    dec.setAttribute("aria-label", "Decrease quantity");
    dec.innerHTML = '<span aria-hidden="true">−</span>';
    dec.addEventListener("click", () => {
      const next = Math.max(1, quantity - 1);
      if (next === quantity) return;
      emit("ws-cart-update", { variantId, quantity: next });
    });

    const qty = document.createElement("div");
    qty.className = "ws-we-cart-qty cart-qty";
    qty.textContent = String(quantity);

    const inc = document.createElement("button");
    inc.type = "button";
    inc.className = "ws-we-inc inc-qty";
    inc.setAttribute("aria-label", "Increase quantity");
    inc.innerHTML = '<span aria-hidden="true">+</span>';
    inc.addEventListener("click", () =>
      emit("ws-cart-update", {
        variantId,
        quantity: Math.min(12, quantity + 1),
      }),
    );

    qtyWrap.append(dec, qty, inc);
    wrap.append(del, qtyWrap);
    return wrap;
  }
}
